import { type FormConfig } from 'FormConfigs';
import moize from 'moize';
import { type ProgramTypeConfig, type WithManualApplication, type WithNetworkAccess } from './Program.types';

/*
    The buildConfig allows us to make a hierarchy of config objects to keep ProgramTypeConfig.ts organized.

    It really only does one thing, which is to convert functions to getters. Doing that allows one function to
    override another function, while also making sure we have the final inputs for each function before we
    ever try to call it.

    Exception: a small set of fields (see `COHORT_ARG_FIELDS`) are function-form `(cohort) => value`
    properties that callers invoke with a cohort context. For those, buildConfig must NOT auto-call on
    access — it returns the function as-is so the caller can pass its own cohort.
*/

const COHORT_ARG_FIELDS = new Set<string>([
    'fullTitle',
    'programTitle',
    'transcriptProgramTitle',
    'programBoxTitle',
    'programSwitcherMenuTitle',
    'programAchievementGraphicProgramTitleLines',
    'acceptedScreenTitleHtml',
    'studentDashboardProgramBoxTitleHtml',
    'supportsScheduleSidebarBox',
    'supportsScheduleStatusSidebarBox',
    'supportsEnrollmentSidebar',
]);

// Extract the base config without the discriminated unions
type BaseConfig = Omit<ProgramTypeConfig, keyof WithManualApplication | keyof WithNetworkAccess>;

type BaseConfigArgs = {
    [key in keyof BaseConfig]: ((args: ProgramTypeConfig) => BaseConfig[key]) | BaseConfig[key];
};

// Enforce the discriminated union: when supportsManuallyApplyingToProgram is true, applicationFormConfigs is required
type ManualApplicationConfigArgs =
    | {
          supportsManuallyApplyingToProgram: true;
          applicationFormConfigs: FormConfig[];
      }
    | {
          supportsManuallyApplyingToProgram?: false;
          applicationFormConfigs: readonly [];
      };

// Enforce the discriminated union: when supportsNetworkAccess is true, networkProgramTitle is required
type NetworkAccessConfigArgs =
    | {
          supportsNetworkAccess: true;
          networkProgramTitle: ((args: ProgramTypeConfig) => string) | string;
      }
    | {
          supportsNetworkAccess?: false;
          networkProgramTitle?: null;
      };

type ConfigArgs = BaseConfigArgs & ManualApplicationConfigArgs & NetworkAccessConfigArgs;

const handleConfigErrors = (e: Error, prop: string) => {
    if (e?.name === 'RangeError') {
        const recursiveError = new Error(
            `Dynamic config properties can not be directly dependent on each other, check the use of "${prop}"`,
        );
        recursiveError.name = 'RecursiveConfigPropertyError';

        throw recursiveError;
    }
    throw e;
};

export default function buildConfig<T extends ProgramTypeConfig>(config: ConfigArgs): ProgramTypeConfig {
    return new Proxy(config as T, {
        get: moize((target: T, prop: string, r: unknown) => {
            const key = prop as keyof typeof target;
            const val = target[key];

            // Cohort-arg function-form fields: return the function reference unchanged so
            // callers can invoke it with their own cohort context (e.g. `config.fullTitle(cohort)`).
            if (COHORT_ARG_FIELDS.has(prop)) {
                return val;
            }

            if (typeof val === 'function') {
                try {
                    return val(r);
                } catch (e) {
                    handleConfigErrors(e as Error, prop);
                }
            }
            return val;
        }),
    });
}
